import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, Calendar } from "lucide-react";
import { MaskedLine } from "./Reveal";
import { DoctoralTam, Flourish, GoldRule } from "./ornaments";
import { scrollToId } from "../lib/scroll";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yFlourishL = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yFlourishR = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, 140]);

  return (
    <section id="hero" ref={ref} data-testid="hero" className="relative flex min-h-screen items-center overflow-hidden bg-navy pt-28 pb-16">
      <motion.div style={{ y: yGlow }} className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute left-1/2 top-[-30%] h-[80vh] w-[80vw] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.14),transparent_60%)]" />
        <div className="absolute bottom-[-20%] left-[-10%] h-[50vh] w-[40vw] rounded-full bg-[radial-gradient(ellipse_at_center,rgba(20,40,80,0.5),transparent_70%)]" />
      </motion.div>

      <motion.div style={{ y: yFlourishL }} className="pointer-events-none absolute -left-10 top-1/3 w-40 opacity-60 sm:w-64" aria-hidden="true">
        <Flourish className="w-full" />
      </motion.div>
      <motion.div style={{ y: yFlourishR }} className="pointer-events-none absolute -right-10 bottom-1/4 w-40 opacity-60 sm:w-64" aria-hidden="true">
        <Flourish className="w-full" flip />
      </motion.div>

      <motion.div
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1.4, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="pointer-events-none absolute right-2 top-0 origin-top sm:right-6"
        aria-hidden="true"
      >
        <div className="animate-sway" style={{ transformOrigin: "top center" }}>
          <DoctoralTam className="h-56 sm:h-72 lg:h-96 w-auto" />
        </div>
      </motion.div>

      <div className="relative z-10 mx-auto max-w-6xl px-5 text-center sm:px-8">
        <MaskedLine delay={0.15}>
          <p className="text-[11px] uppercase tracking-[0.45em] text-gold sm:text-xs" data-testid="hero-eyebrow">
            You are cordially invited
          </p>
        </MaskedLine>

        <MaskedLine delay={0.35} className="mt-6">
          <span className="font-script text-4xl text-gold-light sm:text-6xl lg:text-7xl">Introducing</span>
        </MaskedLine>

        <MaskedLine delay={0.55} className="mt-2">
          <h1 className="font-serif text-5xl font-medium leading-[1.05] text-ivory sm:text-7xl lg:text-8xl" data-testid="hero-name">
            Dr. Stephanie Darilus
          </h1>
        </MaskedLine>

        <div className="relative mt-4 sm:mt-6">
          <MaskedLine delay={0.75}>
            <span className="text-gold-foil animate-gold-shimmer block font-serif text-[clamp(3rem,10.5vw,8.75rem)] font-bold uppercase leading-none tracking-[0.02em]" data-testid="hero-title">
              Graduation
            </span>
          </MaskedLine>
          <MaskedLine delay={1.0} className="-mt-4 sm:-mt-8">
            <span className="font-script block rotate-[-4deg] text-4xl text-gold-light sm:text-6xl lg:text-7xl" data-testid="hero-subtitle">
              Celebration
            </span>
          </MaskedLine>
          <MaskedLine delay={1.1}>
            <p className="mt-6 text-[10px] uppercase tracking-[0.35em] text-mist sm:text-xs" data-testid="hero-degree">
              Doctorate in Business Administration · Florida International University
            </p>
          </MaskedLine>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.25, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 sm:mt-12"
        >
          <GoldRule className="justify-center" />
          <div className="mt-6 flex flex-col items-center justify-center gap-3 text-sm text-mist sm:flex-row sm:gap-8" data-testid="hero-details">
            <span className="flex items-center gap-2">
              <Calendar size={15} className="text-gold" />
              Saturday, December 5
            </span>
            <span className="hidden h-1 w-1 rotate-45 bg-gold sm:block" />
            <span className="flex items-center gap-2">
              <MapPin size={15} className="text-gold" />
              Sarasota, Florida
            </span>
            <span className="hidden h-1 w-1 rotate-45 bg-gold sm:block" />
            <span className="text-gold-light/90 italic font-serif text-base">address shared after RSVP</span>
          </div>

          <div className="mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <button
              data-testid="hero-rsvp-cta"
              onClick={() => scrollToId("rsvp")}
              className="rounded-full bg-gradient-to-r from-gold-pale via-gold to-gold-deep px-9 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-navy-deep transition-all duration-300 hover:scale-[1.04] hover:shadow-[0_0_40px_rgba(212,175,55,0.35)] active:scale-95"
            >
              Join the celebration
            </button>
            <button
              data-testid="hero-stream-cta"
              onClick={() => scrollToId("livestream")}
              className="rounded-full border border-gold/40 px-9 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-gold-light transition-all duration-300 hover:border-gold hover:bg-gold/10"
            >
              Watch the ceremony live
            </button>
          </div>
        </motion.div>
      </div>

      <div className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.4em] text-mist/50">
        Scroll
      </div>
    </section>
  );
};
